import type { GetProgramPageQuery } from "@amzn/ring-neighbors-contentful-gql-schema";
import GET_PROGRAM_PAGE from "@amzn/ring-neighbors-contentful-gql-schema/operations/cpp/getProgramPage.graphql";
import { gql, useQuery } from "@apollo/client";
import { leadSourceType } from "../components/Form";
import { DefaultSuccessMessage } from "../components/Confirmation/DefaultSuccessMessage";

// Form-level UI config stored as a JSON field on CppFormLayout. Queried locally
// (not via the shared gql-schema package) so new UI config keys can be added by
// authoring Contentful + reading them here, without changing the shared operation.
interface FormConfig {
  submitButtonText?: string;
  // When set to a non-empty string, on a successful submit the form shows this
  // text inline right above the Submit button (in Ring green) instead of swapping
  // in the full success message. It's just a JSON key on CppFormLayout.formConfig
  // — no content-model or shared schema change — so a single campaign opts in by
  // authoring the message in Contentful.
  inlineSuccessMessage?: string;
}

const GET_FORM_CONFIG = gql`
  query getFormConfig($id: String!) {
    cppFormLayout(id: $id) {
      formConfig
    }
  }
`;

const mapProgramPageData = (rawData: GetProgramPageQuery | undefined) => {
  if (rawData && rawData.cppFormLayout) {
    return {
      programName: rawData.cppFormLayout.programHeader?.programName,
      programImageURL: rawData.cppFormLayout.programHeader?.programImage
        ?.url as string,
      programPageDescription:
        rawData.cppFormLayout.programHeader?.programPageDescription,
      leadSource: rawData.cppFormLayout.leadSource as string,
      compositeLeadSource: rawData.cppFormLayout
        .compositeLeadSource as leadSourceType,
      organizationType: rawData.cppFormLayout.organizationType as string,
      country: rawData.cppFormLayout.country as string,
      formFields: rawData.cppFormLayout.formFieldsCollection?.items as [],
      redirectionUrlPath: rawData.cppFormLayout.redirectionUrlPath as string,
      programDescription:
        rawData.cppFormLayout.programFooter?.programDescription?.json,
      startDate: rawData.cppFormLayout.startDate,
      endDate: rawData.cppFormLayout.endDate,
      programHeaderDescription:
        rawData.cppFormLayout.programHeader?.programDescription?.json,
      successMessage:
        rawData.cppFormLayout.successMessage?.json || DefaultSuccessMessage,
      isEmbeddedHTML: rawData.cppFormLayout.isEmbeddedHtml as boolean,
    };
  }
  return null;
};

export const useProgramPage = (id: string | undefined) => {
  const { data, loading, ...rest } = useQuery<GetProgramPageQuery>(
    GET_PROGRAM_PAGE,
    {
      variables: { id },
      skip: id == null,
    }
  );

  const { data: formConfigData, loading: formConfigLoading } = useQuery<{
    cppFormLayout?: { formConfig?: FormConfig | null } | null;
  }>(GET_FORM_CONFIG, {
    variables: { id },
    skip: id == null,
  });

  const programPage = mapProgramPageData(data);

  return {
    ...rest,
    // Gate on both queries so the configured submit-button text is present on the
    // first render, avoiding a "Send" -> configured-text flicker. The formConfig
    // query is tiny and runs in parallel, so the added latency is negligible.
    // Note: formConfig's *error* is intentionally not surfaced — a missing/failed
    // formConfig must degrade to the "Send" fallback, not look like a form error.
    loading: loading || formConfigLoading,
    data: programPage
      ? {
          ...programPage,
          formConfig: formConfigData?.cppFormLayout?.formConfig ?? null,
        }
      : programPage,
  };
};
